import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import { GqlObservationStatus } from "~/__generated__";
import { MapEditorResearchTypeValue } from "~/modules/mapEditor/components/MapEditorResearchTypeTree";

const FILTER_DEFINITIONS: Record<
    keyof ProjectMapEditorFilterVars,
    { encode: (value: any) => string; decode: (value: string) => any }
> = {
    ecologicalFunctionTypeIds: {
        encode: (value: string[]) => value.join(","),
        decode: (value: string) => value.split(","),
    },
    observationTypeIds: {
        encode: (value: string[]) => value.join(","),
        decode: (value: string) => value.split(","),
    },
    researchType: {
        encode: (value: MapEditorResearchTypeValue[]) => btoa(JSON.stringify(value)),
        decode: (value: string) => JSON.parse(atob(value)),
    },
    observationStatus: {
        encode: (value: GqlObservationStatus) => value,
        decode: (value: string) => value as GqlObservationStatus,
    },
    visitIds: {
        encode: (value: string[]) => value.join(","),
        decode: (value: string) => value.split(","),
    },
    visitNumber: {
        encode: (value: string[]) => value.join(","),
        decode: (value: string) => value.split(","),
    },
    protocolVisitIds: {
        encode: (value: string[]) => value.join(","),
        decode: (value: string) => value.split(","),
    },
};

export interface ProjectMapEditorFilterVars {
    ecologicalFunctionTypeIds: string[] | undefined;
    observationTypeIds: string[] | undefined;
    researchType: MapEditorResearchTypeValue[] | undefined;
    observationStatus: GqlObservationStatus | undefined;
    visitIds: string[] | undefined;
    visitNumber: string[] | undefined;
    protocolVisitIds: string[] | undefined;
}

export function useMapFilters() {
    const [searchParams, setSearchParams] = useSearchParams();

    const filters = useMemo<ProjectMapEditorFilterVars>(() => {
        const decodedSearchParams = [...searchParams]
            .filter(([label, _]) => label in FILTER_DEFINITIONS)
            .map(([label, value]) => {
                const definition = FILTER_DEFINITIONS[label as keyof typeof FILTER_DEFINITIONS];
                const decoded = definition.decode(value);

                return [label, decoded];
            });

        return Object.fromEntries(decodedSearchParams);
    }, [searchParams]);

    const setFilter = useCallback(
        <K extends keyof ProjectMapEditorFilterVars>(key: K, value: ProjectMapEditorFilterVars[K] | undefined) => {
            setSearchParams(current => {
                const newParams = new URLSearchParams(current);

                if (value === undefined) {
                    newParams.delete(key);
                }

                if (value !== undefined) {
                    const definition = FILTER_DEFINITIONS[key];

                    newParams.set(key, definition.encode(value));
                }

                return newParams;
            });
        },
        [setSearchParams],
    );

    return { filters, setFilter };
}
